import React from "react";
import { Box, Container, Heading, Text, VStack, SimpleGrid, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, Icon, List, ListItem, ListIcon } from "@chakra-ui/react";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";
import { FaClipboardCheck, FaChartBar, FaUmbrella, FaHandHoldingUsd, FaCheckCircle } from "react-icons/fa";

const FinanceWealthPlanning: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/finance/wealth/planning"
        fallbackTitle="Wealth Planning"
        fallbackDescription="Comprehensive financial planning for your future"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={brandConfig.colors.primary} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Text fontSize="sm" color="rgba(255, 255, 255, 0.7)" textTransform="uppercase" letterSpacing="wider">
              Finance Section
            </Text>
            <Heading size="2xl" fontWeight="bold">
              Financial Planning
            </Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="2xl">Plan your financial future</Text>
          </VStack>
        </Container>
      </Box>

      {/* Content Section */}
      <Container maxW="container.xl" py={20} flex="1">
        <VStack spacing={16}>
          {/* Introduction */}
          <Box textAlign="center" maxW="3xl" mx="auto">
            <Heading size="xl" mb={4}>Comprehensive Financial Planning</Heading>
            <Text fontSize="lg" color="gray.600">
              Our holistic approach to financial planning covers all aspects of your financial life,
              from budgeting and debt management to retirement and estate planning.
            </Text>
          </Box>

          {/* Planning Services Grid */}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} w="full">
            <Box bg="blue.50" p={6} borderRadius="lg" textAlign="center" border="1px solid" borderColor="blue.200">
              <Icon as={FaClipboardCheck} boxSize={12} color="blue.600" mb={3} mx="auto" />
              <Heading size="sm" mb={2} color="blue.800">Goal Setting</Heading>
              <Text fontSize="sm" color="gray.600">Define and prioritize your financial objectives</Text>
            </Box>
            <Box bg="green.50" p={6} borderRadius="lg" textAlign="center" border="1px solid" borderColor="green.200">
              <Icon as={FaChartBar} boxSize={12} color="green.600" mb={3} mx="auto" />
              <Heading size="sm" mb={2} color="green.800">Cash Flow Analysis</Heading>
              <Text fontSize="sm" color="gray.600">Optimize income and expenses for growth</Text>
            </Box>
            <Box bg="orange.50" p={6} borderRadius="lg" textAlign="center" border="1px solid" borderColor="orange.200">
              <Icon as={FaUmbrella} boxSize={12} color="orange.600" mb={3} mx="auto" />
              <Heading size="sm" mb={2} color="orange.800">Risk Protection</Heading>
              <Text fontSize="sm" color="gray.600">Insurance and contingency planning</Text>
            </Box>
            <Box bg="purple.50" p={6} borderRadius="lg" textAlign="center" border="1px solid" borderColor="purple.200">
              <Icon as={FaHandHoldingUsd} boxSize={12} color="purple.600" mb={3} mx="auto" />
              <Heading size="sm" mb={2} color="purple.800">Retirement Planning</Heading>
              <Text fontSize="sm" color="gray.600">Secure your retirement lifestyle</Text>
            </Box>
          </SimpleGrid>

          {/* Planning Process Accordion */}
          <Box w="full">
            <Heading size="lg" textAlign="center" mb={8}>Our Planning Process</Heading>
            <Accordion allowToggle>
              <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="lg" mb={4}>
                <AccordionButton p={6} _hover={{ bg: "gray.50" }}>
                  <Box flex={1} textAlign="left">
                    <Heading size="md">Step 1: Discovery Meeting</Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={6} px={6}>
                  <Text color="gray.700" mb={4}>
                    We begin by understanding your current financial situation, goals, and concerns through an in-depth consultation.
                  </Text>
                  <List spacing={2}>
                    <ListItem><ListIcon as={FaCheckCircle} color="green.500" />Review current assets and liabilities</ListItem>
                    <ListItem><ListIcon as={FaCheckCircle} color="green.500" />Discuss short and long-term goals</ListItem>
                    <ListItem><ListIcon as={FaCheckCircle} color="green.500" />Assess risk tolerance and preferences</ListItem>
                    <ListItem><ListIcon as={FaCheckCircle} color="green.500" />Identify planning priorities</ListItem>
                  </List>
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="lg" mb={4}>
                <AccordionButton p={6} _hover={{ bg: "gray.50" }}>
                  <Box flex={1} textAlign="left">
                    <Heading size="md">Step 2: Analysis & Strategy</Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={6} px={6}>
                  <Text color="gray.700" mb={4}>
                    Our team analyzes your data and develops a customized strategy aligned with your objectives.
                  </Text>
                  <List spacing={2}>
                    <ListItem><ListIcon as={FaCheckCircle} color="green.500" />Comprehensive financial analysis</ListItem>
                    <ListItem><ListIcon as={FaCheckCircle} color="green.500" />Scenario modeling and projections</ListItem>
                    <ListItem><ListIcon as={FaCheckCircle} color="green.500" />Strategy recommendations</ListItem>
                    <ListItem><ListIcon as={FaCheckCircle} color="green.500" />Action plan development</ListItem>
                  </List>
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="lg" mb={4}>
                <AccordionButton p={6} _hover={{ bg: "gray.50" }}>
                  <Box flex={1} textAlign="left">
                    <Heading size="md">Step 3: Presentation & Agreement</Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={6} px={6}>
                  <Text color="gray.700" mb={4}>
                    We present your personalized financial plan and ensure you understand every recommendation.
                  </Text>
                  <List spacing={2}>
                    <ListItem><ListIcon as={FaCheckCircle} color="green.500" />Detailed plan presentation</ListItem>
                    <ListItem><ListIcon as={FaCheckCircle} color="green.500" />Explanation of strategies</ListItem>
                    <ListItem><ListIcon as={FaCheckCircle} color="green.500" />Answer all questions</ListItem>
                    <ListItem><ListIcon as={FaCheckCircle} color="green.500" />Refine plan based on feedback</ListItem>
                  </List>
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="lg">
                <AccordionButton p={6} _hover={{ bg: "gray.50" }}>
                  <Box flex={1} textAlign="left">
                    <Heading size="md">Step 4: Implementation & Review</Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={6} px={6}>
                  <Text color="gray.700" mb={4}>
                    We help you implement the plan and provide ongoing support through regular reviews.
                  </Text>
                  <List spacing={2}>
                    <ListItem><ListIcon as={FaCheckCircle} color="green.500" />Execute recommendations</ListItem>
                    <ListItem><ListIcon as={FaCheckCircle} color="green.500" />Coordinate with specialists</ListItem>
                    <ListItem><ListIcon as={FaCheckCircle} color="green.500" />Annual review meetings</ListItem>
                    <ListItem><ListIcon as={FaCheckCircle} color="green.500" />Adjust as life changes</ListItem>
                  </List>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </Box>

          {/* CTA Section */}
          <Box w="full" bg="blue.600" color="white" p={10} borderRadius="xl" textAlign="center">
            <Heading size="lg" mb={4}>Start Your Financial Plan Today</Heading>
            <Text fontSize="lg" mb={6} maxW="2xl" mx="auto">
              Take control of your financial future with a comprehensive plan tailored to your unique goals and circumstances.
            </Text>
            <Text fontSize="md" fontWeight={600}>Book a complimentary discovery session</Text>
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default FinanceWealthPlanning;
